import { apiClient } from './api';
import { ApiResponse } from '../types';

export interface SimulationBot {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  guidance: string;
  ai_model: {
    provider: string;
    model: string;
    temperature: number;
    max_tokens: number;
  };
  status: 'active' | 'inactive';
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Simulation {
  id: string;
  organization_id: string;
  bot_id: string;
  agent_id: string;
  conversation_id: string | null;
  status: 'pending' | 'running' | 'completed' | 'stopped' | 'failed';
  config: {
    delay_ms: number;
    max_turns: number;
    run_until_tasks_complete?: boolean;
  };
  total_turns: number;
  error_message: string | null;
  started_at: string | null;
  stopped_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  bot_name?: string;
  agent_name?: string;
}

export interface SimulationMessage {
  id: string;
  conversation_id: string;
  sender_type: 'customer' | 'agent' | 'system';
  content: string;
  created_at: string;
}

class SimulationApiService {
  // Bot CRUD
  async getBots(): Promise<SimulationBot[]> {
    const response = await apiClient.get<ApiResponse<SimulationBot[]>>('/bots');
    return response.data.data || [];
  }

  async getBot(id: string): Promise<SimulationBot> {
    const response = await apiClient.get<ApiResponse<SimulationBot>>(`/bots/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Bot not found');
  }

  async createBot(data: Partial<SimulationBot>): Promise<SimulationBot> {
    const response = await apiClient.post<ApiResponse<SimulationBot>>('/bots', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create bot');
  }

  async updateBot(id: string, data: Partial<SimulationBot>): Promise<SimulationBot> {
    const response = await apiClient.patch<ApiResponse<SimulationBot>>(`/bots/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update bot');
  }

  async deleteBot(id: string): Promise<void> {
    await apiClient.delete(`/bots/${id}`);
  }

  // Simulation operations
  async getSimulations(): Promise<Simulation[]> {
    const response = await apiClient.get<ApiResponse<Simulation[]>>('/simulations');
    return response.data.data || [];
  }

  async getSimulation(id: string): Promise<Simulation> {
    const response = await apiClient.get<ApiResponse<Simulation>>(`/simulations/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Simulation not found');
  }

  async startSimulation(data: {
    bot_id: string;
    agent_id: string;
    config?: { delay_ms?: number; max_turns?: number; run_until_tasks_complete?: boolean };
  }): Promise<Simulation> {
    const response = await apiClient.post<ApiResponse<Simulation>>('/simulations/start', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to start simulation');
  }

  async advanceSimulation(id: string): Promise<Simulation> {
    const response = await apiClient.post<ApiResponse<Simulation>>(`/simulations/${id}/advance`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to advance simulation');
  }

  async stopSimulation(id: string): Promise<Simulation> {
    const response = await apiClient.post<ApiResponse<Simulation>>(`/simulations/${id}/stop`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to stop simulation');
  }

  async getSimulationMessages(id: string): Promise<SimulationMessage[]> {
    const response = await apiClient.get<ApiResponse<SimulationMessage[]>>(`/simulations/${id}/messages`);
    return response.data.data || [];
  }
}

export const simulationService = new SimulationApiService();
